import type { PropsWithChildren } from "react";
import React from "react";
import { Platform } from "react-native";
import { FontSource, useFonts } from "expo-font";
import type { TextStyle } from "react-native";

const AppFonts = {
  // Inter
  "Inter-Regular": require("../../assets/fonts/inter/Inter_Regular.ttf") as FontSource,
  "Inter-Medium": require("../../assets/fonts/inter/Inter_Medium.ttf") as FontSource,
  "Inter-SemiBold": require("../../assets/fonts/inter/Inter_SemiBold.ttf") as FontSource,
  "Inter-Bold": require("../../assets/fonts/inter/Inter_Bold.ttf") as FontSource,
};

export type AppFontType = keyof typeof AppFonts;

export const AppFontFamily = Object.keys(AppFonts).reduce(
  (acc, font) => {
    acc[font as AppFontType] = font as AppFontType;
    return acc;
  },
  {} as Record<AppFontType, AppFontType>,
);

const WEIGHT_TO_FAMILY: Readonly<Record<string, string>> = {
  "400": AppFontFamily["Inter-Regular"],
  normal: AppFontFamily["Inter-Regular"],
  "500": AppFontFamily["Inter-Medium"],
  "600": AppFontFamily["Inter-SemiBold"],
  "700": AppFontFamily["Inter-Bold"],
  bold: AppFontFamily["Inter-Bold"],
};

/**
 * Resolve a fontWeight to the matching Inter fontFamily name.
 * Passed to a3s-ui's FontFamilyProvider so extracted components render
 * with the app's custom Inter font on native.
 */
export function resolveFontFamily(weight: NonNullable<TextStyle["fontWeight"]>): string | undefined {
  return WEIGHT_TO_FAMILY[String(weight)];
}

export const FontsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [fontsLoaded] = useFonts(AppFonts);

  if (Platform.OS === "web") {
    // Avoid blocking initial render on web if font loading stalls.
    return <>{children}</>;
  }

  if (!fontsLoaded) {
    return null;
  }

  return <>{children}</>;
};
